<template>
  <div v-if="isOpen" class="i18n-modal-overlay" @click.self="closeModal">
    <div class="i18n-modal-content">
      <!-- Header -->
      <div class="i18n-modal-header">
        <div class="i18n-modal-title-group">
          <span class="i18n-modal-icon">✎</span>
          <h2>Edit Translation</h2>
        </div>
        <button class="i18n-modal-close" @click="closeModal" aria-label="Close">✕</button>
      </div>

      <!-- Body -->
      <div class="i18n-modal-body">
        <!-- Key Info -->
        <div class="i18n-form-group">
          <label class="i18n-label">Translation Key</label>

          <!-- Single key — no ambiguity -->
          <div v-if="matchingKeys.length <= 1" class="i18n-key-display">
            <span class="i18n-key-icon">⌗</span>
            <span class="i18n-key-text">{{ currentKey }}</span>
          </div>

          <!-- Multiple matches — searchable dropdown -->
          <div v-else class="i18n-key-ambiguous">
            <div class="i18n-key-ambiguous-hint">
              <span>⚠</span>
              The sub-key <strong>{{ currentSubKey }}</strong> was found under
              {{ matchingKeys.length }} different sections. Choose which one to edit:
            </div>

            <!-- Searchable dropdown -->
            <div class="i18n-searchable-select" v-click-outside="closeDropdown">
              <!-- Trigger -->
              <div
                class="i18n-searchable-trigger"
                :class="{ 'i18n-searchable-trigger--open': dropdownOpen }"
                @click="toggleDropdown"
              >
                <span class="i18n-key-icon">⌗</span>
                <span class="i18n-searchable-value">{{ currentKey }}</span>
                <span class="i18n-searchable-arrow">{{ dropdownOpen ? '▲' : '▼' }}</span>
              </div>

              <!-- Dropdown panel -->
              <div v-if="dropdownOpen" class="i18n-searchable-panel">
                <div class="i18n-searchable-search-wrap">
                  <span class="i18n-searchable-search-icon">🔍</span>
                  <input
                    ref="searchInput"
                    v-model="keySearch"
                    class="i18n-searchable-search"
                    placeholder="Search keys..."
                    @click.stop
                  />
                  <button
                    v-if="keySearch"
                    class="i18n-searchable-clear"
                    @click.stop="keySearch = ''"
                  >✕</button>
                </div>
                <ul class="i18n-searchable-list">
                  <li
                    v-for="k in filteredKeys"
                    :key="k"
                    class="i18n-searchable-item"
                    :class="{ 'i18n-searchable-item--active': currentKey === k }"
                    @click="onSelectKey(k)"
                  >
                    <span class="i18n-searchable-item-icon">⌗</span>
                    <span class="i18n-searchable-item-text" v-html="highlightMatch(k)"></span>
                    <span v-if="currentKey === k" class="i18n-searchable-item-check">✓</span>
                  </li>
                  <li v-if="filteredKeys.length === 0" class="i18n-searchable-empty">
                    No keys match "{{ keySearch }}"
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Auto Translate Bar -->
        <div class="i18n-autotranslate-bar">
          <div class="i18n-autotranslate-source">
            <label class="i18n-label">Auto-translate from</label>
            <select v-model="translateSourceLang" class="i18n-select" :disabled="true">
              <option
                v-for="lang in Languages"
                :key="lang.code"
                :value="lang.code"
                :disabled="!(editedValues[lang.code] || '').trim()"
              >
                {{ getFlagEmoji(lang.flag) }} {{ lang.name }}
                {{ !(editedValues[lang.code] || '').trim() ? '(empty)' : '' }}
              </option>
            </select>
          </div>
          <button
            class="i18n-btn i18n-btn-translate"
            :disabled="isTranslating || !canAutoTranslate"
            @click="autoTranslateAll"
          >
            <span v-if="isTranslating" class="i18n-spinner"></span>
            <span v-else>✦</span>
            {{ isTranslating ? "Translating..." : "Auto Translate All" }}
          </button>
        </div>

        <!-- Dynamic Language Editors -->
        <div class="i18n-dual-editors" :style="{ gridTemplateColumns: editorGridColumns }">
          <template v-for="(lang, index) in Languages" :key="lang.code">
            <div class="i18n-lang-block">
              <div class="i18n-lang-header">
                <span class="i18n-lang-flag">{{ getFlagEmoji(lang.flag) }}</span>
                <span class="i18n-lang-name">{{ lang.name }}</span>
                <span class="i18n-lang-code">{{ lang.code }}</span>
                <button
                  v-if="lang.code !== translateSourceLang"
                  class="i18n-btn-single-translate"
                  :disabled="isTranslating || !canTranslateTo(lang.code)"
                  @click="translateSingle(lang.code)"
                  :title="`Translate to ${lang.name}`"
                >
                  <span v-if="translatingLang === lang.code" class="i18n-spinner i18n-spinner-sm"></span>
                  <span v-else>✦</span>
                </button>
              </div>
              <textarea
                v-model="editedValues[lang.code]"
                class="i18n-textarea"
                :class="{ 'i18n-textarea-rtl': lang.rtl === 'true' }"
                rows="5"
                :placeholder="`Enter ${lang.name} translation...`"
                :dir="lang.rtl === 'true' ? 'rtl' : 'ltr'"
              ></textarea>
              <small class="i18n-hint">
                {{ (editedValues[lang.code] || '').length }} / 10000 characters
              </small>
            </div>

            <div v-if="index < Languages.length - 1" class="i18n-lang-divider">
              <span class="i18n-divider-icon">⇄</span>
            </div>
          </template>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="i18n-error">
          <span>⚠</span> {{ errorMessage }}
        </div>

        <!-- Success Message -->
        <div v-if="successMessage" class="i18n-success">
          <span>✓</span> {{ successMessage }}
        </div>
      </div>

      <!-- Footer -->
      <div class="i18n-modal-footer">
        <button @click="closeModal" class="i18n-btn i18n-btn-cancel">Cancel</button>
        <button
          :disabled="isSaving || !hasAnyValue"
          class="i18n-btn i18n-btn-save"
          @click="saveTranslation"
        >
          <span v-if="isSaving" class="i18n-spinner"></span>
          {{ isSaving ? "Saving..." : "Save All" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { getI18n } from "../plugins/i18nEditorPlugin";
import Translate from "@/API/Translate/Translate";
import Language from "@/API/Language/Language";

export default defineComponent({
  name: "I18nEditorModal",

  // ─── v-click-outside directive ────────────────────────────────────────
  directives: {
    clickOutside: {
      mounted(el, binding) {
        el.__clickOutsideHandler__ = (e) => {
          if (!el.contains(e.target)) binding.value();
        };
        document.addEventListener("mousedown", el.__clickOutsideHandler__);
      },
      unmounted(el) {
        document.removeEventListener("mousedown", el.__clickOutsideHandler__);
      },
    },
  },

  setup() {
    const translateApi = new Translate();
    const languageApi = new Language();
    return { translateApi, languageApi };
  },

  data() {
    return {
      isOpen: false,
      currentKey: "",
      currentLocale: "",
      editedValues: {},
      isSaving: false,
      isTranslating: false,
      translatingLang: null,
      translateSourceLang: "",
      errorMessage: "",
      successMessage: "",
      currentMainKey: "",
      currentSubKey: "",
      originalSubKey: "",
      currentKeyTranslation: null,
      Languages: [],
      matchingKeys: [],
      // searchable dropdown
      dropdownOpen: false,
      keySearch: "",
    };
  },

  computed: {
    hasAnyValue() {
      return this.Languages.some(
        (lang) => (this.editedValues[lang.code] || "").trim()
      );
    },
    editorGridColumns() {
      if (!this.Languages.length) return "1fr";
      return this.Languages
        .map((_, i) => i < this.Languages.length - 1 ? "1fr auto" : "1fr")
        .join(" ");
    },
    canAutoTranslate() {
      return (
        this.translateSourceLang &&
        (this.editedValues[this.translateSourceLang] || "").trim() &&
        this.Languages.length > 1
      );
    },
    filteredKeys() {
      if (!this.keySearch.trim()) return this.matchingKeys;
      const q = this.keySearch.toLowerCase();
      return this.matchingKeys.filter((k) => k.toLowerCase().includes(q));
    },
  },

  async mounted() {
    const raw = await this.languageApi.getAll();
    this.Languages = [...raw].sort((a, b) => a.order - b.order);
    this.initEditedValues();

    if (this.Languages.length) {
      this.translateSourceLang = this.Languages[0].code;
    }

    window.__I18N_EDITOR__ = {
      openModal: this.openModal,
      closeModal: this.closeModal,
    };
  },

  methods: {
    // ─── Init ─────────────────────────────────────────────────────────────
    initEditedValues() {
      const values = {};
      this.Languages.forEach((lang) => (values[lang.code] = ""));
      this.editedValues = values;
    },

    // ─── Searchable dropdown ───────────────────────────────────────────────
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
      if (this.dropdownOpen) {
        this.keySearch = "";
        this.$nextTick(() => this.$refs.searchInput?.focus());
      }
    },
    closeDropdown() {
      this.dropdownOpen = false;
      this.keySearch = "";
    },
    async onSelectKey(key) {
      this.closeDropdown();
      await this.selectKey(key);
    },
    highlightMatch(text) {
      if (!this.keySearch.trim()) return text;
      const escaped = this.keySearch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      return text.replace(
        new RegExp(`(${escaped})`, "gi"),
        '<mark class="i18n-highlight">$1</mark>'
      );
    },

    // ─── Helpers ──────────────────────────────────────────────────────────
    getFlagEmoji(flagCode) {
      if (!flagCode) return "🏳";
      return flagCode
        .toUpperCase()
        .replace(/./g, (char) =>
          String.fromCodePoint(127397 + char.charCodeAt(0))
        );
    },

    subKeyToReadable(subKey) {
      if (!subKey) return "";
      return subKey
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },

    canTranslateTo(targetCode) {
      if (targetCode === this.translateSourceLang) return false;
      return (this.editedValues[this.translateSourceLang] || "").trim().length > 0;
    },

    // ─── Find all full keys that share this subKey in i18n messages ───────
    findAllMatchingKeys(subKey) {
      const i18n = getI18n();
      if (!i18n) return [];

      const locale = i18n.global.locale.value;
      const messages = i18n.global.getLocaleMessage(locale);
      const results = [];

      const walk = (obj, path) => {
        if (!obj || typeof obj !== "object") return;
        for (const [key, value] of Object.entries(obj)) {
          const fullPath = path ? `${path}.${key}` : key;
          if (typeof value === "string") {
            if (fullPath.split(".").pop() === subKey) results.push(fullPath);
          } else if (typeof value === "object" && value !== null) {
            walk(value, fullPath);
          }
        }
      };

      walk(messages, "");
      return results;
    },

    // ─── Select a key ─────────────────────────────────────────────────────
    async selectKey(key) {
      this.currentKey = key;
      const parts = key.split(".");
      this.currentMainKey = parts[0];
      this.currentSubKey = parts.slice(1).join(".");
      this.currentKeyTranslation = null;
      this.initEditedValues();
      await this.loadAllLocales();
    },

    // ─── Google Translate ─────────────────────────────────────────────────
    async googleTranslate(text, targetLang, sourceLang) {
      const url =
        `https://translate.googleapis.com/translate_a/single` +
        `?client=gtx` +
        `&sl=${encodeURIComponent(sourceLang)}` +
        `&tl=${encodeURIComponent(targetLang)}` +
        `&dt=t` +
        `&q=${encodeURIComponent(text)}`;

      const response = await fetch(url);
      if (!response.ok) throw new Error(`Translation failed (${response.status})`);
      const data = await response.json();
      return data[0].map((chunk) => chunk[0]).filter(Boolean).join("");
    },

    // ─── Auto Translate All ───────────────────────────────────────────────
    async autoTranslateAll() {
      if (!this.canAutoTranslate) return;
      this.isTranslating = true;
      this.errorMessage = "";
      const sourceText = (this.editedValues[this.translateSourceLang] || "").trim();
      const targets = this.Languages.filter((l) => l.code !== this.translateSourceLang);
      try {
        const results = await Promise.all(
          targets.map((lang) =>
            this.googleTranslate(sourceText, lang.code, this.translateSourceLang)
          )
        );
        targets.forEach((lang, i) => { this.editedValues[lang.code] = results[i]; });
      } catch (error) {
        this.errorMessage = `Translation failed: ${error.message}`;
      } finally {
        this.isTranslating = false;
      }
    },

    // ─── Translate Single ─────────────────────────────────────────────────
    async translateSingle(targetCode) {
      if (!this.canTranslateTo(targetCode)) return;
      this.translatingLang = targetCode;
      this.isTranslating = true;
      this.errorMessage = "";
      const sourceText = (this.editedValues[this.translateSourceLang] || "").trim();
      try {
        this.editedValues[targetCode] = await this.googleTranslate(
          sourceText, targetCode, this.translateSourceLang
        );
      } catch (error) {
        this.errorMessage = `Translation failed: ${error.message}`;
      } finally {
        this.translatingLang = null;
        this.isTranslating = false;
      }
    },

    // ─── Open Modal ───────────────────────────────────────────────────────
    async openModal(key, locale, value) {
      this.currentKey = key;
      this.currentLocale = locale;
      this.currentKeyTranslation = null;
      this.errorMessage = "";
      this.successMessage = "";
      this.matchingKeys = [];
      this.dropdownOpen = false;
      this.keySearch = "";
      this.initEditedValues();

      const parts = this.currentKey?.split(".");
      this.currentMainKey = parts[0];
      this.currentSubKey = parts.slice(1).join(".");
      this.originalSubKey = this.currentSubKey;

      this.isOpen = true;

      const found = this.findAllMatchingKeys(this.originalSubKey);
      if (!found.includes(this.currentKey)) found.unshift(this.currentKey);
      this.matchingKeys = found;

      await this.loadAllLocales();

      this.$nextTick(() => {
        document.querySelector(".i18n-textarea")?.focus();
      });
    },

    // ─── Load All Locales ─────────────────────────────────────────────────
    async loadAllLocales() {
      try {
        const result = await this.translateApi.getAll({
          filter: `name|${this.currentMainKey}|=`,
        });
        if (result && result.length > 0) {
          const translationObj = result[0];
          this.currentKeyTranslation = translationObj;
          for (const lang of this.Languages) {
            const languageData = translationObj[lang.code];
            if (Array.isArray(languageData)) {
              const matchedItem = languageData.find((item) =>
                item.hasOwnProperty(this.currentSubKey)
              );
              if (matchedItem) {
                this.editedValues[lang.code] = matchedItem[this.currentSubKey] || "";
              }
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch translations:", error);
      }

      const enLang = this.Languages.find((l) => l.code === "en");
      if (enLang && !(this.editedValues["en"] || "").trim()) {
        this.editedValues["en"] = this.subKeyToReadable(this.currentSubKey);
        this.translateSourceLang = "en";
      } else {
        const firstFilled = this.Languages.find(
          (l) => (this.editedValues[l.code] || "").trim()
        );
        if (firstFilled) this.translateSourceLang = firstFilled.code;
      }
    },

    // ─── Close Modal ──────────────────────────────────────────────────────
    closeModal() {
      this.isOpen = false;
      this.initEditedValues();
      this.errorMessage = "";
      this.successMessage = "";
      this.currentKeyTranslation = null;
      this.matchingKeys = [];
      this.dropdownOpen = false;
      this.keySearch = "";
    },

    // ─── Save Translation ─────────────────────────────────────────────────
    async saveTranslation() {
      if (!this.hasAnyValue) {
        this.errorMessage = "Please provide at least one translation value.";
        return;
      }
      this.isSaving = true;
      this.errorMessage = "";
      this.successMessage = "";

      try {
        if (!this.currentKeyTranslation) {
          this.currentKeyTranslation = { name: this.currentMainKey };
          this.Languages.forEach((lang) => { this.currentKeyTranslation[lang.code] = []; });
        }

        for (const lang of this.Languages) {
          const value = (this.editedValues[lang.code] || "").trim();
          if (!value) continue;
          if (!Array.isArray(this.currentKeyTranslation[lang.code])) {
            this.currentKeyTranslation[lang.code] = [];
          }
          const languageData = this.currentKeyTranslation[lang.code];
          const existingIndex = languageData.findIndex((item) =>
            item.hasOwnProperty(this.currentSubKey)
          );
          if (existingIndex >= 0) {
            languageData[existingIndex][this.currentSubKey] = value;
          } else {
            languageData.push({ [this.currentSubKey]: value });
          }
        }

        if (this.currentKeyTranslation?.id) {
          await this.translateApi.update({ ...this.currentKeyTranslation });
        } else {
          await this.translateApi.insert({ ...this.currentKeyTranslation });
        }

        try {
          const i18n = getI18nInstance();
          for (const lang of this.Languages) {
            const value = (this.editedValues[lang.code] || "").trim();
            if (!value) continue;
            const messages = i18n.global.getLocaleMessage(lang.code);
            this.setNestedProperty(messages, this.currentKey, value);
            i18n.global.setLocaleMessage(lang.code, messages);
          }
        } catch { /* silent */ }

        this.successMessage = "Translations saved successfully!";
        this.$emit("translation-saved", {
          key: this.currentKey,
          values: { ...this.editedValues },
        });
        setTimeout(() => this.closeModal(), 1500);
      } catch (error) {
        this.errorMessage =
          error.response?.data?.error || error.message || "An error occurred while saving";
      } finally {
        this.isSaving = false;
      }
    },

    // ─── Utility ──────────────────────────────────────────────────────────
    setNestedProperty(obj, path, value) {
      const keys = path.split(".");
      let current = obj;
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!current[key] || typeof current[key] !== "object") current[key] = {};
        current = current[key];
      }
      current[keys[keys.length - 1]] = value;
    },
  },
});

function getI18nInstance() {
  return getI18n();
}
</script>

<style scoped>
.i18n-modal-overlay {
  position: fixed; inset: 0;
  background-color: rgba(0, 0, 0, 0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999999; animation: fadeIn 0.25s ease; backdrop-filter: blur(2px);
}
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

.i18n-modal-content {
  background: #fff; border-radius: 12px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.18);
  width: 92%; max-width: 780px; max-height: 90vh;
  display: flex; flex-direction: column;
  animation: slideUp 0.25s ease-out; overflow: hidden;
}
@keyframes slideUp {
  from { transform: translateY(16px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}

/* ── Header ─────────────────────────────── */
.i18n-modal-header {
  padding: 18px 22px; border-bottom: 1px solid #efefef;
  display: flex; justify-content: space-between; align-items: center;
  background: #fafafa;
}
.i18n-modal-title-group { display: flex; align-items: center; gap: 10px; }
.i18n-modal-icon { font-size: 20px; color: #1F5B8F; }
.i18n-modal-header h2 { margin: 0; font-size: 17px; font-weight: 700; color: #222; }
.i18n-modal-close {
  background: none; border: none; font-size: 20px; cursor: pointer;
  color: #aaa; line-height: 1; padding: 4px 8px; border-radius: 6px;
  transition: background 0.15s, color 0.15s;
}
.i18n-modal-close:hover { background: #f0f0f0; color: #333; }

/* ── Body ────────────────────────────────── */
.i18n-modal-body {
  flex: 1; padding: 22px; overflow-y: auto;
  display: flex; flex-direction: column; gap: 20px;
}
.i18n-form-group { display: flex; flex-direction: column; gap: 8px; }
.i18n-label {
  font-size: 12px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.06em; color: #888;
}
.i18n-key-display {
  display: flex; align-items: center; gap: 8px;
  background: #f5f3fa; border: 1px solid #e0d6f0;
  border-radius: 6px; padding: 9px 14px;
}
.i18n-key-icon { color: #5290c5; font-size: 15px; flex-shrink: 0; }
.i18n-key-text { font-size: 18px; color: #5a2d75; font-weight: 700; word-break: break-all; }

/* ── Ambiguous hint ───────────────────────── */
.i18n-key-ambiguous { display: flex; flex-direction: column; gap: 10px; }
.i18n-key-ambiguous-hint {
  display: flex; align-items: center; gap: 8px;
  padding: 9px 13px; background: #fff8e1;
  border: 1px solid #ffe082; border-radius: 8px;
  font-size: 13px; color: #795548;
}
.i18n-key-ambiguous-hint span:first-child { font-size: 16px; flex-shrink: 0; }

/* ── Searchable dropdown ──────────────────── */
.i18n-searchable-select { position: relative; width: 100%; }

.i18n-searchable-trigger {
  display: flex; align-items: center; gap: 8px;
  background: #f5f3fa; border: 2px solid #1F5B8F;
  border-radius: 8px; padding: 10px 14px; cursor: pointer;
  transition: box-shadow 0.18s, border-color 0.18s;
  user-select: none;
}
.i18n-searchable-trigger--open {
  border-color: #5290c5;
  box-shadow: 0 0 0 3px rgba(110, 56, 148, 0.12);
}
.i18n-searchable-trigger:hover { border-color: #5290c5; }
.i18n-searchable-value {
  flex: 1; font-size: 14px; font-weight: 600;
  color: #5a2d75; word-break: break-all;
}
.i18n-searchable-arrow { font-size: 11px; color: #5290c5; flex-shrink: 0; }

.i18n-searchable-panel {
  position: absolute; top: calc(100% + 6px); left: 0; right: 0;
  background: #fff; border: 2px solid #1F5B8F; border-radius: 10px;
  box-shadow: 0 8px 24px rgba(110, 56, 148, 0.15);
  z-index: 100; overflow: hidden;
  animation: dropDown 0.15s ease-out;
}
@keyframes dropDown {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
}

.i18n-searchable-search-wrap {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 12px; border-bottom: 1px solid #f0e8f8;
  background: #faf6ff;
}
.i18n-searchable-search-icon { font-size: 13px; flex-shrink: 0; }
.i18n-searchable-search {
  flex: 1; border: none; background: transparent;
  font-size: 13px; color: #333; outline: none;
}
.i18n-searchable-search::placeholder { color: #bbb; }
.i18n-searchable-clear {
  background: none; border: none; cursor: pointer;
  color: #aaa; font-size: 13px; padding: 0 2px;
  line-height: 1; transition: color 0.15s;
}
.i18n-searchable-clear:hover { color: #1F5B8F; }

.i18n-searchable-list {
  list-style: none; margin: 0; padding: 6px;
  max-height: 220px; overflow-y: auto;
}
.i18n-searchable-item {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 10px; border-radius: 6px; cursor: pointer;
  transition: background 0.12s;
}
.i18n-searchable-item:hover { background: #f3edfb; }
.i18n-searchable-item--active { background: #ede8f7; }
.i18n-searchable-item-icon { color: #5290c5; font-size: 13px; flex-shrink: 0; }
.i18n-searchable-item-text {
  flex: 1; font-size: 13px; font-weight: 600;
  color: #5a2d75; word-break: break-all;
}
.i18n-searchable-item-check { color: #1F5B8F; font-weight: 700; flex-shrink: 0; }
.i18n-searchable-empty {
  padding: 12px 10px; font-size: 13px; color: #aaa; text-align: center;
}

/* highlight match */
:deep(.i18n-highlight) {
  background: #ffe082; color: #5a2d75;
  border-radius: 2px; padding: 0 1px; font-weight: 700;
}

/* ── Auto Translate Bar ───────────────────── */
.i18n-autotranslate-bar {
  display: flex; align-items: flex-end; gap: 12px;
  padding: 14px 16px; background: #f8f4fd;
  border: 1px solid #e4d6f5; border-radius: 10px;
}
.i18n-autotranslate-source { display: flex; flex-direction: column; gap: 6px; flex: 1; }
.i18n-select {
  padding: 8px 12px; border: 1px solid #ddd; border-radius: 8px;
  font-size: 14px; color: #333; background: #fff; cursor: pointer;
  transition: border-color 0.2s;
}
.i18n-select:focus {
  outline: none; border-color: #1F5B8F;
  box-shadow: 0 0 0 3px rgba(110, 56, 148, 0.12);
}
.i18n-btn-translate {
  background: linear-gradient(135deg, #1F5B8F, #5290c5);
  color: #fff; padding: 9px 18px; border: none; border-radius: 8px;
  cursor: pointer; font-size: 14px; font-weight: 600;
  display: flex; align-items: center; gap: 7px;
  white-space: nowrap; transition: opacity 0.18s, transform 0.18s;
}
.i18n-btn-translate:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
.i18n-btn-translate:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Per-language translate button ───────── */
.i18n-btn-single-translate {
  margin-left: auto; background: none; border: 1px solid #d4b8ee;
  border-radius: 6px; color: #1F5B8F; font-size: 13px;
  padding: 3px 8px; cursor: pointer; display: flex; align-items: center;
  gap: 4px; transition: background 0.15s, color 0.15s;
}
.i18n-btn-single-translate:hover:not(:disabled) { background: #1F5B8F; color: #fff; }
.i18n-btn-single-translate:disabled { opacity: 0.4; cursor: not-allowed; }

/* ── Language Editors ─────────────────────── */
.i18n-dual-editors { display: grid; gap: 0; align-items: start; }
.i18n-lang-block { display: flex; flex-direction: column; gap: 8px; }
.i18n-lang-header {
  display: flex; align-items: center; gap: 8px;
  padding-bottom: 6px; border-bottom: 2px solid #f0e8f8;
}
.i18n-lang-flag { font-size: 18px; line-height: 1; }
.i18n-lang-name { font-size: 14px; font-weight: 700; color: #333; }
.i18n-lang-code {
  font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
  color: #fff; background: #1F5B8F; border-radius: 4px; padding: 2px 7px;
}
.i18n-lang-divider {
  display: flex; align-items: center; justify-content: center;
  padding: 0 16px; padding-top: 40px;
}
.i18n-divider-icon { font-size: 20px; color: #ccc; }
.i18n-textarea {
  width: 100%; padding: 10px 12px; border: 1px solid #ddd;
  border-radius: 8px; font-family: inherit; font-size: 14px;
  line-height: 1.6; resize: vertical; min-height: 110px;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box; color: #222;
}
.i18n-textarea:focus {
  outline: none; border-color: #1F5B8F;
  box-shadow: 0 0 0 3px rgba(110, 56, 148, 0.12);
}
.i18n-textarea-rtl { direction: rtl; text-align: right; font-size: 15px; }
.i18n-hint { display: block; color: #bbb; font-size: 11px; text-align: right; }

/* ── Messages ─────────────────────────────── */
.i18n-error, .i18n-success {
  display: flex; align-items: center; gap: 8px;
  padding: 11px 14px; border-radius: 8px; font-size: 13px; font-weight: 500;
}
.i18n-error { background: #fff0f0; color: #c0392b; border: 1px solid #fdd; }
.i18n-success { background: #f0fff4; color: #27ae60; border: 1px solid #cfc; }

/* ── Footer ──────────────────────────────── */
.i18n-modal-footer {
  padding: 16px 22px; border-top: 1px solid #efefef;
  display: flex; justify-content: flex-end; gap: 10px; background: #fafafa;
}
.i18n-btn {
  padding: 9px 22px; border: none; border-radius: 8px; cursor: pointer;
  font-size: 14px; font-weight: 600; transition: all 0.18s;
  display: flex; align-items: center; gap: 7px;
}
.i18n-btn-cancel { background: #ececec; color: #555; }
.i18n-btn-cancel:hover { background: #e0e0e0; }
.i18n-btn-save { background: #1F5B8F; color: #fff; }
.i18n-btn-save:hover:not(:disabled) { background: #5a2d78; }
.i18n-btn-save:disabled { opacity: 0.55; cursor: not-allowed; }

/* ── Spinners ─────────────────────────────── */
.i18n-spinner {
  width: 13px; height: 13px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff; border-radius: 50%;
  animation: spin 0.7s linear infinite; flex-shrink: 0;
}
.i18n-spinner-sm {
  width: 10px; height: 10px;
  border-color: rgba(110, 56, 148, 0.3);
  border-top-color: #1F5B8F;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Responsive ──────────────────────────── */
@media (max-width: 580px) {
  .i18n-dual-editors { grid-template-columns: 1fr !important; gap: 20px; }
  .i18n-lang-divider { display: none; }
  .i18n-autotranslate-bar { flex-direction: column; align-items: stretch; }
}
</style>